import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, MapPin, Shield, CheckCircle2, Menu, X } from "lucide-react";
import api, { formatApiError } from "../api";

/*
 * AUPRÉA — Page Contact dédiée
 * Design aligné sur Landing v3 (banque privée) : Playfair Display + Lato, Navy/Gold/Cream.
 * Formulaire structuré relayé par Brevo via POST /api/contact/submit.
 */

const C = {
  navy: "#1B2A4A",
  gold: "#C8902C",
  goldDark: "#8A5F14",
  goldLight: "#E2B865",
  cream: "#F5F2EC",
  white: "#FFFFFF",
  gray: "#555555",
  line: "#E4DDD0",
};
const FONT_SERIF = "'Playfair Display', Georgia, serif";
const FONT_SANS = "'Lato', 'Helvetica Neue', Arial, sans-serif";

const SUBJECTS = [
  { value: "succession", label: "Succession" },
  { value: "bilan-dirigeants", label: "Bilan dirigeants" },
  { value: "partenariats", label: "Partenariats" },
  { value: "presse", label: "Presse" },
  { value: "autre", label: "Autre" },
];

function Kicker({ children }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
      <span style={{ display: "inline-block", width: 40, height: 1.5, background: C.gold }} />
      <span style={{ fontFamily: FONT_SANS, fontSize: 13, fontWeight: 700, letterSpacing: "0.24em", color: C.gold, textTransform: "uppercase" }}>
        {children}
      </span>
    </div>
  );
}

function Field({ label, required, children, hint, error, testid }) {
  return (
    <label data-testid={testid} style={{ display: "block", marginBottom: 20 }}>
      <div style={{ fontFamily: FONT_SANS, fontSize: 12.5, fontWeight: 700, color: C.navy, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>
        {label}{required && <span style={{ color: C.gold, marginLeft: 4 }}>*</span>}
      </div>
      {children}
      {hint && !error && <div style={{ fontSize: 12.5, color: C.gray, marginTop: 6, fontFamily: FONT_SANS }}>{hint}</div>}
      {error && <div style={{ fontSize: 12.5, color: "#B4531A", marginTop: 6, fontFamily: FONT_SANS, fontWeight: 600 }}>{error}</div>}
    </label>
  );
}

const inputBase = {
  width: "100%",
  padding: "13px 15px",
  fontSize: 15,
  fontFamily: FONT_SANS,
  color: C.navy,
  background: C.white,
  border: `1px solid ${C.line}`,
  borderRadius: 3,
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color .15s ease, box-shadow .15s ease",
};

export default function Contact() {
  const [burger, setBurger] = useState(false);
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth <= 980 : false));
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 980);
    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    document.title = "AUPRÉA — Nous contacter";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
    const prev = meta.getAttribute("content");
    meta.setAttribute("content", "Contactez l'équipe AUPRÉA — succession, bilan dirigeants, partenariats, presse. Réponse sous 48 heures ouvrées.");
    return () => { if (prev != null) meta.setAttribute("content", prev); };
  }, []);

  const [form, setForm] = useState({
    prenom: "",
    nom: "",
    email: "",
    telephone: "",
    sujet: "succession",
    message: "",
    consent: false,
    website: "", // honeypot
  });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => {
    const v = e.target && e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((er) => ({ ...er, [k]: undefined }));
    setServerError("");
  };

  const validate = () => {
    const e = {};
    if (!form.prenom.trim() || form.prenom.trim().length < 2) e.prenom = "Prénom requis (2 caractères minimum).";
    if (!form.nom.trim() || form.nom.trim().length < 2) e.nom = "Nom requis (2 caractères minimum).";
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) e.email = "Email invalide.";
    if (!form.sujet) e.sujet = "Veuillez choisir un sujet.";
    if (!form.message.trim() || form.message.trim().length < 10) e.message = "Message trop court (10 caractères minimum).";
    if (form.message.length > 4000) e.message = "Message trop long (4 000 caractères maximum).";
    if (!form.consent) e.consent = "Votre consentement est requis.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (loading) return;
    if (!validate()) return;
    setLoading(true);
    setServerError("");
    try {
      await api.post("/contact/submit", {
        prenom: form.prenom.trim(),
        nom: form.nom.trim(),
        email: form.email.trim().toLowerCase(),
        telephone: form.telephone.trim() || null,
        sujet: form.sujet,
        message: form.message.trim(),
        consent: form.consent,
        website: form.website, // honeypot
      });
      setSent(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setServerError(formatApiError(err) || "Une erreur est survenue. Réessayez plus tard.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="landing-premium" style={{ background: C.cream, color: C.navy, fontFamily: FONT_SANS, lineHeight: 1.65, fontSize: 16, minHeight: "100vh" }}>
      <style>{`
        .landing-premium { font-feature-settings: "liga","dlig","kern"; }
        .landing-premium .lp-container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
        .landing-premium h1,.landing-premium h2,.landing-premium h3 { font-family: ${FONT_SERIF}; color: ${C.navy}; font-weight: 500; }
        .landing-premium h1 em { font-style: italic; font-weight: 500; color: ${C.gold}; }
        .landing-premium input:focus, .landing-premium textarea:focus, .landing-premium select:focus {
          border-color: ${C.gold} !important;
          box-shadow: 0 0 0 3px ${C.gold}22 !important;
        }
        @media (max-width: 980px) {
          .contact-grid { grid-template-columns: 1fr !important; }
          .contact-row { grid-template-columns: 1fr !important; }
          .nav-desktop { display: none !important; }
        }
      `}</style>

      {/* HEADER (identique à Landing) */}
      <header style={{ background: C.cream, borderBottom: `1px solid ${C.line}`, position: "sticky", top: 0, zIndex: 50 }} data-testid="contact-header">
        <div className="lp-container" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 76, gap: 16 }}>
          <Link to="/" data-testid="brand-logo" style={{ fontFamily: FONT_SERIF, fontWeight: 700, fontSize: 28, color: C.navy, textDecoration: "none", letterSpacing: "0.02em" }}>AUPRÉA</Link>
          {!isMobile && (
            <nav className="nav-desktop" style={{ display: "flex", alignItems: "center", gap: 32 }}>
              <Link to="/inscription" style={navLinkStyle} data-testid="nav-carnet">Le Carnet</Link>
              <Link to="/comment-ca-marche" style={navLinkStyle} data-testid="nav-cmm">Comment ça marche</Link>
              <Link to="/ouvrir-une-succession/diagnostic" style={navLinkStyle} data-testid="nav-succession">Succession en cours</Link>
              <Link to="/tarifs" style={navLinkStyle} data-testid="nav-tarifs">Tarifs</Link>
              <Link to="/connexion" data-testid="btn-nav-login" style={{
                display: "inline-flex", alignItems: "center", gap: 8, padding: "11px 22px",
                border: `1.5px solid ${C.navy}`, borderRadius: 4, color: C.navy, fontSize: 14.5,
                fontWeight: 600, textDecoration: "none",
              }}>Se connecter</Link>
            </nav>
          )}
          {isMobile && (
            <button onClick={() => setBurger((b) => !b)} data-testid="nav-burger" aria-label="Menu" style={{ background: "transparent", border: "none", color: C.navy, cursor: "pointer", padding: 8 }}>
              {burger ? <X size={26} /> : <Menu size={26} />}
            </button>
          )}
        </div>
        {isMobile && burger && (
          <div style={{ background: C.cream, borderTop: `1px solid ${C.line}`, padding: "16px 24px" }}>
            <Link to="/inscription" onClick={() => setBurger(false)} style={{ ...navLinkStyle, display: "block", padding: "10px 0" }}>Le Carnet</Link>
            <Link to="/comment-ca-marche" onClick={() => setBurger(false)} style={{ ...navLinkStyle, display: "block", padding: "10px 0" }}>Comment ça marche</Link>
            <Link to="/ouvrir-une-succession/diagnostic" onClick={() => setBurger(false)} style={{ ...navLinkStyle, display: "block", padding: "10px 0" }}>Succession en cours</Link>
            <Link to="/tarifs" onClick={() => setBurger(false)} style={{ ...navLinkStyle, display: "block", padding: "10px 0" }}>Tarifs</Link>
            <Link to="/connexion" onClick={() => setBurger(false)} style={{ ...navLinkStyle, display: "block", padding: "12px 0", fontWeight: 700 }}>Se connecter</Link>
          </div>
        )}
      </header>

      {/* HERO */}
      <section style={{ padding: "72px 0 32px" }}>
        <div className="lp-container">
          <Kicker>Contact</Kicker>
          <h1 style={{ fontSize: "clamp(36px, 5vw, 56px)", lineHeight: 1.1, margin: "0 0 20px 0", maxWidth: 820 }}>
            Nous sommes à vos <em>côtés.</em>
          </h1>
          <p style={{ fontSize: 18, color: C.gray, maxWidth: 680, margin: 0, lineHeight: 1.7 }}>
            Une question sur la succession, le bilan dirigeants, un partenariat ou la presse ?
            Écrivez-nous. Un membre de l'équipe AUPRÉA vous répond sous <strong style={{ color: C.navy }}>48 heures ouvrées</strong>.
          </p>
        </div>
      </section>

      {/* CONTENU — formulaire + colonne latérale */}
      <section style={{ padding: "32px 0 100px" }}>
        <div className="lp-container">
          <div className="contact-grid" style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 56, alignItems: "start" }}>

            {/* FORMULAIRE */}
            <div style={{ background: C.white, border: `1px solid ${C.line}`, borderRadius: 4, padding: "40px 44px" }}>
              {sent ? (
                <div data-testid="contact-success" style={{ textAlign: "center", padding: "24px 8px" }}>
                  <div style={{ width: 72, height: 72, borderRadius: "50%", background: `${C.gold}22`, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
                    <CheckCircle2 size={38} color={C.gold} strokeWidth={1.6} />
                  </div>
                  <h2 style={{ fontSize: 28, margin: "0 0 12px 0" }}>Message bien reçu.</h2>
                  <p style={{ fontSize: 16, color: C.gray, maxWidth: 460, margin: "0 auto 28px", lineHeight: 1.7 }}>
                    Merci, <strong style={{ color: C.navy }}>{form.prenom}</strong>. Un accusé de réception a été envoyé à <strong style={{ color: C.navy }}>{form.email}</strong>.
                    L'équipe AUPRÉA vous répondra sous 48 heures ouvrées.
                  </p>
                  <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
                    <Link to="/" data-testid="contact-success-home" style={{
                      display: "inline-flex", alignItems: "center", gap: 8, padding: "13px 26px",
                      background: C.gold, color: C.navy, fontSize: 14.5, fontWeight: 700, borderRadius: 4, textDecoration: "none",
                    }}>Retour à l'accueil <ArrowRight size={16} /></Link>
                    <button
                      data-testid="contact-success-new"
                      onClick={() => { setSent(false); setForm({ prenom: "", nom: "", email: "", telephone: "", sujet: "succession", message: "", consent: false, website: "" }); }}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 8, padding: "13px 26px",
                        background: "transparent", color: C.navy, fontSize: 14.5, fontWeight: 600,
                        borderRadius: 4, border: `1.5px solid ${C.navy}`, cursor: "pointer", fontFamily: FONT_SANS,
                      }}>Envoyer un autre message</button>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} noValidate data-testid="contact-form">
                  <h2 style={{ fontSize: 24, margin: "0 0 8px 0" }}>Votre message</h2>
                  <p style={{ fontSize: 14.5, color: C.gray, margin: "0 0 28px 0" }}>Les champs marqués d'un <span style={{ color: C.gold }}>*</span> sont obligatoires.</p>

                  <div className="contact-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    <Field label="Prénom" required error={errors.prenom} testid="contact-field-prenom">
                      <input type="text" value={form.prenom} onChange={set("prenom")} data-testid="contact-input-prenom" maxLength={80} style={inputBase} autoComplete="given-name" />
                    </Field>
                    <Field label="Nom" required error={errors.nom} testid="contact-field-nom">
                      <input type="text" value={form.nom} onChange={set("nom")} data-testid="contact-input-nom" maxLength={80} style={inputBase} autoComplete="family-name" />
                    </Field>
                  </div>

                  <div className="contact-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    <Field label="Email" required error={errors.email} testid="contact-field-email">
                      <input type="email" value={form.email} onChange={set("email")} data-testid="contact-input-email" style={inputBase} autoComplete="email" />
                    </Field>
                    <Field label="Téléphone" hint="Facultatif" error={errors.telephone} testid="contact-field-tel">
                      <input type="tel" value={form.telephone} onChange={set("telephone")} data-testid="contact-input-telephone" maxLength={32} style={inputBase} autoComplete="tel" />
                    </Field>
                  </div>

                  <Field label="Sujet" required error={errors.sujet} testid="contact-field-sujet">
                    <select value={form.sujet} onChange={set("sujet")} data-testid="contact-input-sujet" style={{ ...inputBase, appearance: "none", backgroundImage: `linear-gradient(45deg, transparent 50%, ${C.navy} 50%), linear-gradient(135deg, ${C.navy} 50%, transparent 50%)`, backgroundPosition: `calc(100% - 20px) calc(1em + 2px), calc(100% - 14px) calc(1em + 2px)`, backgroundSize: `6px 6px, 6px 6px`, backgroundRepeat: "no-repeat", paddingRight: 40 }}>
                      {SUBJECTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </Field>

                  <Field label="Message" required error={errors.message} hint={`${form.message.length} / 4000 caractères`} testid="contact-field-message">
                    <textarea rows={7} value={form.message} onChange={set("message")} data-testid="contact-input-message" maxLength={4000} style={{ ...inputBase, resize: "vertical", minHeight: 160, lineHeight: 1.6 }} />
                  </Field>

                  {/* Honeypot — caché aux humains, visible aux bots */}
                  <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}>
                    <label>Votre site web
                      <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
                    </label>
                  </div>

                  <label data-testid="contact-field-consent" style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "16px 18px", background: C.cream, borderRadius: 3, border: `1px solid ${errors.consent ? "#B4531A" : C.line}`, marginBottom: 24, cursor: "pointer" }}>
                    <input type="checkbox" checked={form.consent} onChange={set("consent")} data-testid="contact-input-consent" style={{ marginTop: 3, accentColor: C.gold, cursor: "pointer" }} />
                    <span style={{ fontSize: 13.5, color: C.gray, lineHeight: 1.55 }}>
                      J'accepte qu'AUPRÉA traite les informations ci-dessus pour répondre à ma demande, conformément à la{" "}
                      <Link to="/legal/confidentialite" style={{ color: C.goldDark, textDecoration: "underline" }}>politique de confidentialité</Link>.
                      Mes données ne sont ni revendues ni partagées. <span style={{ color: C.gold }}>*</span>
                    </span>
                  </label>
                  {errors.consent && <div style={{ fontSize: 12.5, color: "#B4531A", marginTop: -16, marginBottom: 20, fontWeight: 600 }}>{errors.consent}</div>}

                  {serverError && (
                    <div data-testid="contact-server-error" style={{ background: "#FBECE3", border: "1px solid #E8A789", color: "#8A4218", padding: "12px 16px", borderRadius: 3, marginBottom: 20, fontSize: 14 }}>
                      {serverError}
                    </div>
                  )}

                  <button type="submit" disabled={loading} data-testid="contact-submit" style={{
                    display: "inline-flex", alignItems: "center", gap: 10, minHeight: 48,
                    background: loading ? C.goldLight : C.gold, color: C.navy, fontFamily: FONT_SANS, fontSize: 15.5, fontWeight: 700,
                    padding: "16px 32px", border: "none", borderRadius: 4,
                    letterSpacing: "0.01em", cursor: loading ? "not-allowed" : "pointer",
                  }}>
                    {loading ? "Envoi en cours…" : "Envoyer le message"} {!loading && <ArrowRight size={18} strokeWidth={1.75} />}
                  </button>
                </form>
              )}
            </div>

            {/* COLONNE LATÉRALE */}
            <aside>
              <div style={{ background: C.navy, color: C.cream, padding: "32px 32px", borderRadius: 4, marginBottom: 24 }}>
                <div style={{ fontSize: 12, letterSpacing: "0.22em", color: C.goldLight, fontWeight: 700, textTransform: "uppercase", marginBottom: 14 }}>Contact direct</div>
                <h3 style={{ fontSize: 22, color: C.cream, margin: "0 0 20px 0", fontFamily: FONT_SERIF, fontWeight: 500 }}>Vous préférez l'email&nbsp;?</h3>

                <div style={{ display: "flex", gap: 14, alignItems: "flex-start", marginBottom: 18 }}>
                  <Mail size={18} color={C.goldLight} strokeWidth={1.6} style={{ marginTop: 2, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 11.5, letterSpacing: "0.16em", color: C.goldLight, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>Email</div>
                    <a href="mailto:contact@auprea.com" data-testid="contact-email-link" style={{ color: C.cream, fontSize: 15.5, textDecoration: "none", borderBottom: `1px solid ${C.goldLight}66` }}>contact@auprea.com</a>
                  </div>
                </div>

                <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                  <MapPin size={18} color={C.goldLight} strokeWidth={1.6} style={{ marginTop: 2, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 11.5, letterSpacing: "0.16em", color: C.goldLight, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>Région</div>
                    <div style={{ color: C.cream, fontSize: 15.5, lineHeight: 1.55 }}>France métropolitaine<br />&amp; Outremer</div>
                  </div>
                </div>
              </div>

              <div style={{ background: C.white, border: `1px solid ${C.line}`, padding: "24px 26px", borderRadius: 4 }}>
                <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12 }}>
                  <Shield size={16} color={C.gold} strokeWidth={1.7} />
                  <div style={{ fontSize: 11.5, letterSpacing: "0.18em", color: C.gold, fontWeight: 700, textTransform: "uppercase" }}>Confidentialité</div>
                </div>
                <p style={{ fontSize: 14, color: C.gray, margin: 0, lineHeight: 1.65 }}>
                  Vos informations sont strictement utilisées pour répondre à votre message.
                  Hébergement européen, aucun revente, suppression possible sur simple demande.
                </p>
              </div>

              <div style={{ marginTop: 24, padding: "20px 24px", background: `${C.gold}10`, borderLeft: `3px solid ${C.gold}`, borderRadius: 2 }}>
                <div style={{ fontSize: 12, color: C.goldDark, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 6 }}>Délai de réponse</div>
                <div style={{ fontSize: 15, color: C.navy, lineHeight: 1.55 }}>
                  Sous <strong>48 heures ouvrées</strong>. Les demandes concernant une succession en cours sont prioritaires.
                </div>
              </div>
            </aside>

          </div>
        </div>
      </section>

      {/* FOOTER MINIMAL */}
      <footer style={{ background: C.navy, color: C.cream, padding: "32px 0" }}>
        <div className="lp-container" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
          <div style={{ fontSize: 13, color: "rgba(245,242,236,0.6)" }}>© 2026 AUPRÉA SAS</div>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: 13 }}>
            <Link to="/legal/mentions-legales" style={{ color: "rgba(245,242,236,0.7)", textDecoration: "none" }}>Mentions légales</Link>
            <Link to="/legal/cgu" style={{ color: "rgba(245,242,236,0.7)", textDecoration: "none" }}>CGU</Link>
            <Link to="/legal/cgv" style={{ color: "rgba(245,242,236,0.7)", textDecoration: "none" }}>CGV</Link>
            <Link to="/legal/confidentialite" style={{ color: "rgba(245,242,236,0.7)", textDecoration: "none" }}>Confidentialité</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

const navLinkStyle = {
  fontFamily: FONT_SANS, fontSize: 14.5, fontWeight: 500, color: C.navy,
  textDecoration: "none", padding: "8px 2px", letterSpacing: "0.01em",
};
